import "react-datepicker/dist/react-datepicker.css";
import { useCallback, useMemo, useState } from "react";
import { ReportApi } from "src/services/api/ReportApi";
import {
    ColDef,
    IServerSideDatasource,
    IServerSideGetRowsParams,
    LoadSuccessParams,
    GridApi,
    SizeColumnsToContentStrategy,
    ValueFormatterParams,
    ValueFormatterFunc,
} from "ag-grid-enterprise";

import useSidebar from "src/hooks/useSidebar";
import useReport from "src/hooks/useReport";

import BaseGrid from "../../BaseGrid";
import LoadingComponent from "src/components/LoadingComponent";
import SearchCriteriaRedReports from "src/components/SearchCriteriaRedReports";
import { downloadFile } from "src/utils/DownloadFile";
import { formatCurrency } from "src/utils/FormatCurrency";
import useReference from "src/hooks/useReference";
import { MonthlyDeclinedTransactionsModel } from "src/models/MonthlyDeclinedTransactionsModel";

const MonthlyDeclinedTransactions = () => {
    const { setIsOpen } = useSidebar();
    const { reportInputs, mapReportRequest, getContextMenuItems } = useReport();

    const [gridReady, setGridReady] = useState(false);
    const [gridApi, setGridApi] = useState<GridApi>();
    const [dataSource, setDataSource] = useState<IServerSideDatasource>();
    const [isDownload, setIsDownload] = useState(false);
    const [isLoading, setIsLoading] = useState(false);

    const { currencyList } = useReference();

    const numericToAlpha = useMemo<Record<string, string>>(() => {
        const map: Record<string, string> = {};
        (currencyList ?? []).forEach((c: any) => {
            if (c?.numericCode != null && c?.alphaCode) {
                map[String(c.numericCode)] = String(c.alphaCode).toUpperCase();
            }
        });
        return map;
    }, [currencyList]);

    const resolveAlphaFromRow = useCallback((data: MonthlyDeclinedTransactionsModel): string => {
        const feeVal = data?.transactionCurrencyCode;
        if (feeVal == null) return "USD";
        const str = String(feeVal).trim();
        if (/^[A-Za-z]{3}$/.test(str)) return str.toUpperCase();
        const mapped = numericToAlpha[String(str)];
        if (mapped) return mapped;
        return "USD";
    }, [numericToAlpha]);

    const currencyFormatter =  useCallback<ValueFormatterFunc<MonthlyDeclinedTransactionsModel, any>>(
        (params: ValueFormatterParams<MonthlyDeclinedTransactionsModel, any>): string => {
        const v = params?.value;
        if (v === null || v === undefined) return "";
        const n = typeof v === "number" ? v : Number(v);
        if (Number.isNaN(n)) return "";
        const alphaCode = resolveAlphaFromRow(params.data as MonthlyDeclinedTransactionsModel);
        return String(formatCurrency(n, alphaCode));
        }, [resolveAlphaFromRow]);

    const colDefs = useMemo<ColDef<MonthlyDeclinedTransactionsModel>[]>(() => [
        { headerName: "ProgramName", field: "programName" },
        { headerName: "MerchantGroupName", field: "merchantGroupName" },
        { headerName: "Merchant", field: "merchant" },
        { headerName: "MerchantDescriptor", field: "merchantDescriptor" },
        { headerName: "ExternalId", field: "externalId" },
        { headerName: "Date", field: "date" },
        { headerName: "Amount", field: "amount", valueFormatter: currencyFormatter },
        { headerName: "DeclineReason", field: "declineReason" },
        { headerName: "SystemTransactionId", field: "systemTransactionId" },
        { headerName: "SystemTransactionType", field: "systemTransactionType" },
        { headerName: "AcceptorCode", field: "acceptorCode" },
        { headerName: "TransactionCurrencyCode", field: "transactionCurrencyCode" },
        { headerName: "TimeZoneStandardNameId", field: "timeZoneStandardNameId" },
        { headerName: "TotalBasketSize", field: "totalBasketSize", valueFormatter: currencyFormatter }     
    ], [currencyFormatter]);

    const defaultColDef: ColDef = {
        sortable: false,
        filter: false,
        cellStyle: { textAlign: "center" },
    };

    const autoSizeStrategy = useMemo<SizeColumnsToContentStrategy>(() => {
        return {
            type: "fitCellContents",
            defaultMinWidth: 120,
            defaultMaxWidth: 300,
            skipHeader: false,
        };
    }, []);

    const handleViewReport = () => {
            setIsDownload(false);
            setGridReady(true);
            setDataSource(createServerSideDatasource());
    };

    const handleDownloadReport = () => {
        setIsDownload(true);
        setIsLoading(true);
        ReportApi.exportMonthlyDeclinedTransactions(mapReportRequest(null))
            .then((res: any) => {
                downloadFile(MonthlyDeclinedTransactions.displayName!, res.data);
            })
            .finally(() => setIsLoading(false));
    };

    const createServerSideDatasource = (): IServerSideDatasource => {
        return {
            getRows: (params: IServerSideGetRowsParams) => {
                getReportData(params);
            },
        };
    };

    const getReportData = (params: IServerSideGetRowsParams | null) => {
        setIsOpen(false);
        
        ReportApi.getMonthlyDeclinedTransactions(mapReportRequest(params))
            .then((res: any) => {
                const result = res.data;

                // Normalize date for grid display
                result?.rows?.forEach((x: MonthlyDeclinedTransactionsModel) => {
                    x.date = x.date
                        ? new Date(x.date).toLocaleDateString()
                        : x.date;
                });

                const success: LoadSuccessParams = {
                    rowData: result.rows,
                    rowCount: result.totalCount,
                };
                if (result?.rows?.length > 0) {
                    params?.api?.hideOverlay();
                    params?.success(success);
                } else {
                    params?.api?.showNoRowsOverlay();
                    params?.success({ rowData: [], rowCount: 0 });
                }
            })
            .catch(() => params?.fail?.());
    };

    return (
        <>
            <div className="report-container">
                <div className="report-header">
                    <h5 className="mb-0">Monthly Declined Transactions Report</h5>
                </div>
                <div className="blocks">
                    <SearchCriteriaRedReports
                        handleViewReport={handleViewReport}
                        handleDownloadReport={handleDownloadReport}
                        showToDate={true}
                        useDevelopers={true}
                    />
                </div>
                {isDownload && isLoading ? (
                    <LoadingComponent />
                ) : (
                    !isDownload && (
                        <BaseGrid
                            colDefs={colDefs}
                            defaultColDef={defaultColDef}
                            getData={getReportData}
                            isGridReady={gridReady}
                            reportInputs={reportInputs}
                            dataSource={dataSource}
                            gridApi={gridApi}
                            setGridApi={setGridApi}
                            getContextMenuItems={getContextMenuItems}
                            autoSizeStrategy={autoSizeStrategy}
                        />
                    )
                )}
            </div>
        </>
    );
};

MonthlyDeclinedTransactions.displayName = "MonthlyDeclinedTransactions";

export default MonthlyDeclinedTransactions;
